import React, { useRef } from 'react';
import { computeCompleteness, formatMemberSince, userInitial } from './profileUtils';
import { formatBytes, resolveAvatarUrl } from '../../utils/media';

const MAX_AVATAR_BYTES = 10 * 1024 * 1024;

const COPY = {
  en: {
    guestTitle: 'Your profile',
    guestLead: 'Sign in to manage your display name, bio, email, and security settings.',
    signIn: 'Sign in',
    verified: 'Email verified',
    unverified: 'Email not verified',
    noEmail: 'No email bound',
    complete: 'Profile complete',
    completeHint: 'Profile {score}/{total} complete',
    completeHintAction: ' · Verify your email for better security',
    completeHintBio: ' · Add a short bio to finish your profile',
    changeAvatar: 'Change photo',
    removeAvatar: 'Remove',
    uploading: 'Uploading…',
    avatarTooLarge: 'Image must be 10 MB or smaller.',
    avatarTypeError: 'Use JPEG, PNG, WebP, or GIF.',
  },
  zh: {
    guestTitle: '个人资料',
    guestLead: '登录后可管理显示名称、简介、邮箱与安全设置。',
    signIn: '登录',
    verified: '邮箱已验证',
    unverified: '邮箱未验证',
    noEmail: '尚未绑定邮箱',
    complete: '资料已完善',
    completeHint: '资料完成度 {score}/{total}',
    completeHintAction: ' · 绑定并验证邮箱可提升账户安全',
    completeHintBio: ' · 填写简介即可完成资料',
    changeAvatar: '更换头像',
    removeAvatar: '移除',
    uploading: '上传中…',
    avatarTooLarge: '图片不能超过 10 MB。',
    avatarTypeError: '请使用 JPEG、PNG、WebP 或 GIF 图片。',
  },
};

function GuestHero({ lang, onLogin }) {
  const t = COPY[lang === 'zh' ? 'zh' : 'en'];
  return (
    <div className="profile-v2-hero profile-v2-hero-guest profile-v2-enter">
      <div className="profile-v2-guest-icon" aria-hidden="true">
        <svg viewBox="0 0 64 64" width="56" height="56" fill="none">
          <circle cx="32" cy="22" r="12" stroke="currentColor" strokeWidth="2" />
          <path
            d="M12 54c0-11 9-18 20-18s20 7 20 18"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
          />
        </svg>
      </div>
      <div className="profile-v2-hero-text">
        <h1 className="profile-v2-hero-title">{t.guestTitle}</h1>
        <p className="profile-v2-hero-lead">{t.guestLead}</p>
        <button type="button" className="profile-v2-hero-cta" onClick={onLogin}>
          {t.signIn}
        </button>
      </div>
    </div>
  );
}

function ProfileIdentityHero({
  lang,
  displayName,
  email,
  emailVerified,
  memberSince,
  bio,
  avatarUrl,
  avatarVersion,
  avatarUploading,
  avatarStats,
  onLogin,
  onAvatarSelected,
  onAvatarRemove,
  onAvatarError,
  isGuest,
}) {
  const t = COPY[lang === 'zh' ? 'zh' : 'en'];
  const fileInputRef = useRef(null);
  const resolvedAvatarUrl = resolveAvatarUrl(avatarUrl, avatarVersion);

  if (isGuest) {
    return <GuestHero lang={lang} onLogin={onLogin} />;
  }

  const { score, total } = computeCompleteness({
    name: displayName,
    bio,
    emailVerified,
  });
  const pct = Math.round((score / total) * 100);
  const isComplete = score === total;

  let hint = t.completeHint.replace('{score}', score).replace('{total}', total);
  if (!isComplete) {
    if (!emailVerified) {
      hint += t.completeHintAction;
    } else if (!String(bio || '').trim()) {
      hint += t.completeHintBio;
    }
  }

  let statusLabel = t.noEmail;
  let statusClass = 'is-muted';
  if (email) {
    statusLabel = emailVerified ? t.verified : t.unverified;
    statusClass = emailVerified ? 'is-verified' : 'is-unverified';
  }

  const handleFileChange = (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    if (file.size > MAX_AVATAR_BYTES) {
      if (typeof onAvatarError === 'function') onAvatarError(t.avatarTooLarge);
      return;
    }
    const allowed = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
    if (!allowed.includes(file.type)) {
      if (typeof onAvatarError === 'function') onAvatarError(t.avatarTypeError);
      return;
    }
    if (typeof onAvatarSelected === 'function') {
      onAvatarSelected(file);
    }
  };

  return (
    <div className="profile-v2-hero profile-v2-enter">
      <div className="profile-v2-hero-main">
        <div className="profile-v2-avatar-wrap">
          <div className={`profile-v2-avatar${resolvedAvatarUrl ? ' has-image' : ''}`}>
            {resolvedAvatarUrl ? (
              <img src={resolvedAvatarUrl} alt="" className="profile-v2-avatar-img" />
            ) : (
              userInitial(displayName)
            )}
          </div>
          <div className="profile-v2-avatar-actions">
            <button
              type="button"
              className="profile-v2-avatar-btn"
              onClick={() => fileInputRef.current?.click()}
              disabled={avatarUploading}
            >
              {avatarUploading ? t.uploading : t.changeAvatar}
            </button>
            {avatarUrl ? (
              <button
                type="button"
                className="profile-v2-avatar-btn profile-v2-avatar-btn-muted"
                onClick={() => typeof onAvatarRemove === 'function' && onAvatarRemove()}
                disabled={avatarUploading}
              >
                {t.removeAvatar}
              </button>
            ) : null}
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp,image/gif"
            className="profile-v2-avatar-input"
            onChange={handleFileChange}
          />
        </div>
        <div className="profile-v2-hero-info">
          <h1 className="profile-v2-hero-title">{displayName}</h1>
          <div className="profile-v2-hero-meta">
            <span className={`profile-v2-status-pill ${statusClass}`}>{statusLabel}</span>
            {memberSince ? (
              <span className="profile-v2-hero-since">{formatMemberSince(memberSince, lang)}</span>
            ) : null}
          </div>
          {avatarStats ? (
            <p className="profile-v2-avatar-stats">
              {lang === 'zh'
                ? `已压缩：${formatBytes(avatarStats.originalBytes)} → ${formatBytes(avatarStats.storedBytes)}`
                : `Compressed: ${formatBytes(avatarStats.originalBytes)} → ${formatBytes(avatarStats.storedBytes)}`}
            </p>
          ) : null}
        </div>
      </div>
      <div className="profile-v2-completeness">
        {isComplete ? (
          <p className="profile-v2-completeness-label profile-v2-completeness-done">
            <span className="profile-v2-check" aria-hidden="true">✓</span>
            {t.complete}
          </p>
        ) : (
          <>
            <div
              className="profile-v2-completeness-track"
              role="progressbar"
              aria-valuenow={score}
              aria-valuemin={0}
              aria-valuemax={total}
              aria-label={hint}
            >
              <div className="profile-v2-completeness-fill" style={{ width: `${pct}%` }} />
            </div>
            <p className="profile-v2-completeness-label">{hint}</p>
          </>
        )}
      </div>
    </div>
  );
}

export default ProfileIdentityHero;
